/**
 * TraderLiveWatch — Real-time options synthetics and straddle pricing
 *
 * Uses MarketDataContext (WebSocket from Durable Object) for ALL live data.
 * The VPS pushes enriched ticks including ATM, CE/PE premiums, straddle price,
 * and synthetic spot — all computed server-side from live Greeksoft ticks.
 */
import { useState, useEffect, useMemo, useRef } from 'react'
import { useMarketData } from '../../context/MarketDataContext'

// Token mapping (same as MarketDataContext NAME_TO_TOKEN but reversed)
const TOKEN_MAP: Record<string, { token: string; exchange: string }> = {
  NIFTY:      { token: '101999957', exchange: 'NSE' },
  BANKNIFTY:  { token: '101999972', exchange: 'NSE' },
  FINNIFTY:   { token: '101999923', exchange: 'NSE' },
  SENSEX:     { token: '100000034', exchange: 'BSE' },
  BANKEX:     { token: '100000035', exchange: 'BSE' },
  MIDCPNIFTY: { token: '101999999', exchange: 'NSE' },
  INDIAVIX:   { token: '999920005', exchange: 'NSE' },
}

const INDICES = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'SENSEX', 'BANKEX', 'MIDCPNIFTY']
const OPTION_INDICES = new Set(['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY', 'SENSEX', 'BANKEX'])

function getTickData(ticks: Record<string, any>, symbol: string) {
  const info = TOKEN_MAP[symbol]
  if (!info) return null
  const tick = ticks[info.token]
  if (!tick || !tick.ltp) return null
  return tick
}

export function TraderLiveWatch() {
  const { ticks, connected, tickCount } = useMarketData()
  const [lastUpdate, setLastUpdate] = useState(Date.now())

  // Update timestamp when new ticks arrive
  useEffect(() => {
    if (tickCount > 0) setLastUpdate(Date.now())
  }, [tickCount])

  // Check if we have any real data
  const hasData = useMemo(() => {
    return INDICES.some(sym => {
      const tick = getTickData(ticks, sym)
      return tick && tick.ltp > 0
    })
  }, [ticks])

  if (!connected && !hasData) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '400px' }}>
        <div style={{ padding: '2rem 4rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
          Connecting to live data stream...
        </div>
      </div>
    )
  }

  const vixToken = '999920005'
  // VIX data from live WebSocket ticks
  const vixTick = ticks[vixToken]
  const vixSpot = vixTick?.ltp || 0

  // ── RSI (14-period, 1-minute candles, Wilder's smoothing) ──
  const rsiStateRef = useRef<{
    currentCandle: { close: number; minute: number } | null;
    candles: number[];           // 1-minute close prices
    avgGain: number;
    avgLoss: number;
    initialized: boolean;        // true after 14 candles processed
  }>({ currentCandle: null, candles: [], avgGain: 0, avgLoss: 0, initialized: false });
  const [vixRsi, setVixRsi] = useState<number | null>(null);

  useEffect(() => {
    if (vixSpot <= 0) return;
    const state = rsiStateRef.current;
    const now = new Date();
    const minute = now.getHours() * 60 + now.getMinutes();

    // Build 1-minute candles from tick stream
    if (!state.currentCandle || state.currentCandle.minute !== minute) {
      // Close previous candle and push to history
      if (state.currentCandle) {
        state.candles.push(state.currentCandle.close);

        const len = state.candles.length;
        if (len >= 2) {
          const diff = state.candles[len - 1] - state.candles[len - 2];
          const gain = diff > 0 ? diff : 0;
          const loss = diff < 0 ? -diff : 0;

          if (!state.initialized && len >= 15) {
            // SMA seed: average of first 14 changes
            let sumG = 0, sumL = 0;
            for (let i = 1; i < len; i++) {
              const d = state.candles[i] - state.candles[i - 1];
              if (d > 0) sumG += d; else sumL -= d;
            }
            state.avgGain = sumG / 14;
            state.avgLoss = sumL / 14;
            state.initialized = true;
          } else if (state.initialized) {
            // Wilder's smoothing
            state.avgGain = (state.avgGain * 13 + gain) / 14;
            state.avgLoss = (state.avgLoss * 13 + loss) / 14;
          }

          if (state.initialized) {
            const rsi = state.avgLoss === 0
              ? (state.avgGain > 0 ? 100 : 50)
              : 100 - (100 / (1 + state.avgGain / state.avgLoss));
            setVixRsi(Math.round(rsi * 10) / 10);
          }
        }

        // Keep last 100 candles max
        if (state.candles.length > 100) state.candles.shift();
      }
      state.currentCandle = { close: vixSpot, minute };
    } else {
      // Update current candle's close with latest tick
      state.currentCandle.close = vixSpot;
    }
  }, [vixSpot]);

  return (
    <div>
      <style dangerouslySetInnerHTML={{__html: `
        .lw-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 1rem;
        }
        .lw-widget-title { font-size: 0.9375rem; font-weight: 700; color: var(--text-primary); }
        .lw-spot-price { font-size: 1.5rem; font-weight: 800; line-height: 1; letter-spacing: -0.5px; color: var(--text-primary); }
        .lw-flex-between { display: flex; justify-content: space-between; align-items: center; }
        .lw-inner-pill {
          background: var(--bg-elevated);
          border: 1px solid var(--border);
          padding: 0.375rem 0.625rem;
          border-radius: 6px;
          display: flex; align-items: center; gap: 0.5rem;
        }
        .lw-premium-split { display: flex; gap: 0.375rem; background: var(--bg-default); border: 1px solid var(--border); border-radius: 6px; padding: 0.25rem; }
        .lw-premium-leg { flex: 1; padding: 0.5rem; border-radius: 4px; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
        .lw-ce { background: rgba(34, 197, 94, 0.05); border: 1px solid rgba(34, 197, 94, 0.2); }
        .lw-pe { background: rgba(239, 68, 68, 0.05); border: 1px solid rgba(239, 68, 68, 0.2); }
        .lw-total { background: var(--bg-elevated); border: 1px solid var(--border); }
        .lw-val-sm { font-size: 0.625rem; color: var(--text-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.02em; }
        .lw-val-lg { font-size: 0.875rem; font-weight: 700; color: var(--text-primary); }
        .lw-gauge-container { height: 8px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 999px; overflow: hidden; margin-top: 0.375rem; position: relative; }
        .lw-gauge-fill { height: 100%; border-radius: 999px; transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
        .lw-rsi-green { background: var(--green); }
        .lw-rsi-red { background: var(--red); }
        .lw-rsi-neutral { background: var(--yellow); }
        .lw-live-badge {
          display: inline-flex; align-items: center; gap: 0.25rem;
          font-size: 0.5625rem; font-weight: 700; letter-spacing: 0.08em;
          color: var(--green); text-transform: uppercase;
        }
        .lw-live-dot {
          width: 5px; height: 5px; border-radius: 50%;
          background: var(--green); animation: pulse-dot 2s infinite;
        }
      `}} />

      <div className="page-header">
        <div>
          <h2 className="page-title">LiveWatch Dashboard</h2>
          <p className="page-subtitle">
            Real-time options synthetics and straddle pricing
            {connected && <span className="lw-live-badge" style={{ marginLeft: '0.75rem' }}>
              <span className="lw-live-dot" /> LIVE — WebSocket
            </span>}
          </p>
        </div>
      </div>
      
      <div className="lw-grid">
        
        {/* VIX Hero Widget */}
        <div className="card" style={{ gridColumn: '1 / -1', padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div className="lw-flex-between">
            <div>
              <div className="lw-widget-title">INDIA VIX</div>
              <div className="lw-spot-price" style={{ marginTop: '0.125rem' }}>
                {vixSpot > 0 ? vixSpot.toFixed(2) : '—'}
              </div>
              {vixTick && vixTick.pChange !== 0 && (
                <span style={{
                  fontSize: '0.75rem', fontWeight: 600, marginTop: '0.25rem', display: 'inline-block',
                  color: vixTick.change >= 0 ? 'var(--green)' : 'var(--red)',
                }}>
                  {vixTick.change > 0 ? '+' : ''}{vixTick.change?.toFixed(2)} ({vixTick.pChange > 0 ? '+' : ''}{vixTick.pChange?.toFixed(2)}%)
                </span>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="lw-val-sm">RSI (14)</div>
              <div className="lw-spot-price" style={{
                fontSize: '1.75rem', marginTop: '0.125rem',
                color: vixRsi === null ? 'var(--text-muted)' : vixRsi > 70 ? 'var(--red)' : vixRsi < 30 ? 'var(--green)' : 'var(--yellow)',
              }}>
                {vixRsi === null ? '—' : vixRsi.toFixed(1)}
              </div>
            </div>
          </div>
          <div>
            <div className="lw-flex-between lw-val-sm" style={{ marginBottom: '0.125rem', fontSize: '0.5625rem' }}>
              <span>0</span><span>30</span><span>70</span><span>100</span>
            </div>
            <div className="lw-gauge-container">
              <div className={`lw-gauge-fill ${!vixRsi ? 'lw-rsi-neutral' : vixRsi > 70 ? 'lw-rsi-red' : vixRsi < 30 ? 'lw-rsi-green' : 'lw-rsi-neutral'}`}
                style={{ width: `${Math.min(Math.max(vixRsi || 0, 0), 100)}%` }} />
            </div>
          </div>
        </div>

        {/* Index Widgets */}
        {INDICES.map(underlying => {
          const tick = getTickData(ticks, underlying)
          const spot = tick?.ltp || 0
          const change = tick?.change || 0
          const changePct = tick?.pChange || 0
          const baseAtm = (tick as any)?.baseAtm || 0
          const cePremium = (tick as any)?.cePremium
          const pePremium = (tick as any)?.pePremium
          const straddlePrice = (tick as any)?.straddlePrice
          const syntheticSpot = (tick as any)?.syntheticSpot
          const syntheticAtm = (tick as any)?.syntheticAtm
          const hasSpot = spot > 0

          if (!hasSpot) {
            // Even for indices without spot yet — show a waiting card
            // (SENSEX/BANKEX should arrive from VPS REST poller within a few seconds)
            return (
              <div key={underlying} className="card" style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.875rem', opacity: 0.5 }}>
                <div className="lw-flex-between" style={{ alignItems: 'center' }}>
                  <div className="lw-widget-title">{underlying}</div>
                  <div className="lw-inner-pill" style={{ padding: '0.25rem 0.5rem' }}>
                    <span className="lw-val-sm">Awaiting data...</span>
                  </div>
                </div>
              </div>
            )
          }

          const hasOptions = OPTION_INDICES.has(underlying)

          return (
            <div key={underlying} className="card" style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              
              <div className="lw-flex-between" style={{ alignItems: 'center' }}>
                <div className="lw-widget-title">{underlying}</div>
                <div className="lw-inner-pill" style={{ padding: '0.25rem 0.5rem', display: 'flex', alignItems: 'baseline' }}>
                  <span className="lw-val-sm" style={{ marginRight: '0.25rem' }}>Spot</span>
                  <span className="lw-val-lg" style={{ fontSize: '0.8125rem' }}>{spot.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  {change !== 0 && (
                    <span style={{ 
                      fontSize: '0.625rem', fontWeight: 600, marginLeft: '0.5rem',
                      color: change >= 0 ? 'var(--green)' : 'var(--red)'
                    }}>
                      {change > 0 ? '+' : ''}{change.toFixed(2)} ({changePct > 0 ? '+' : ''}{changePct.toFixed(2)}%)
                    </span>
                  )}
                </div>
              </div>

              <div className="lw-inner-pill" style={{ justifyContent: 'center', padding: '0.375rem' }}>
                <span className="lw-val-sm">Base ATM</span>
                <span className="lw-val-lg" style={{ marginLeft: '0.375rem' }}>{baseAtm || '—'}</span>
              </div>

              {/* Premium Pills — only for option indices */}
              {hasOptions && (
              <div className="lw-premium-split">
                <div className="lw-premium-leg lw-ce">
                  <span className="lw-val-sm">CE Prem</span>
                  <span className="lw-val-lg" style={{ color: 'var(--green)' }}>
                    {cePremium ? `₹${cePremium.toFixed(1)}` : '—'}
                  </span>
                </div>
                <div className="lw-premium-leg lw-total">
                  <span className="lw-val-sm">Straddle</span>
                  <span className="lw-val-lg">
                    {straddlePrice ? `₹${straddlePrice.toFixed(1)}` : '—'}
                  </span>
                </div>
                <div className="lw-premium-leg lw-pe">
                  <span className="lw-val-sm">PE Prem</span>
                  <span className="lw-val-lg" style={{ color: 'var(--red)' }}>
                    {pePremium ? `₹${pePremium.toFixed(1)}` : '—'}
                  </span>
                </div>
              </div>
              )}

              {/* Synthetic Tracker — only for option indices */}
              {hasOptions && (
              <div className="lw-flex-between" style={{ gap: '0.5rem' }}>
                <div className="lw-inner-pill" style={{ flex: 1, flexDirection: 'column', alignItems: 'flex-start', background: 'transparent', border: 'none', padding: '0' }}>
                  <span className="lw-val-sm">Synthetic Spot</span>
                  <span className="lw-val-lg" style={{ fontSize: '1.125rem' }}>
                    {syntheticSpot ? syntheticSpot.toFixed(2) : '—'}
                  </span>
                </div>
                <div className="lw-inner-pill">
                  <span className="lw-val-sm">True ATM</span>
                  <span className="lw-val-lg" style={{ marginLeft: '0.375rem', color: 'var(--accent)' }}>
                    {syntheticAtm || '—'}
                  </span>
                </div>
              </div>
              )}

            </div>
          )
        })}

      </div>
    </div>
  )
}
